import React, { useEffect, useState } from 'react';
import {
  Tabs,
  styled,
  Tab,
  Theme,
  Button,
  Box,
  Menu,
  MenuItem,
  Typography,
  Popover,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../redux/hooks';
import {
  deleteSingleNoteRequest,
  fetchNotesRequest,
  setDeleteModalOpen,
  clearNotes,
  clearNoteCache,
  setSelectedNoteId,
  setInitialHtml,
  setDeleteRecordingLabel,
} from '../../redux/note/noteSlice';
import { TabsProperties } from '../../types/notes';
import { formatNumberToStringMonthWithoutYear } from '../../utils/dateUtils';
import RichTextEditor from './TextEditor';
import NotesLoading from './NotesLoading';
import DeleteNoteConfirmation from './DeleteNoteConfirmation';
import createPermissionCheckers from '../../permissions-validation/createPermissionCheckers';
import { UserPermissionType } from '../../types/commonTypes';

interface IStyledProps {
  theme?: Theme;
  selected?: boolean;
}

const SingleTab = styled(Tab)<IStyledProps>(({ theme, selected }) => ({
  minHeight: '34px',
  padding: '8px 0px',
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'row',
  alignItems: 'center',
  fontFamily: 'Satoshi',
  fontSize: '13px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '18px',
  borderRadius: '10px 10px 0px 0px',
  color: theme.palette.primary.main,
  marginRight: '5px',
  background: 'var(--Accent-White, #FFF)',
  boxShadow: '0px 4px 40px 0px rgba(255, 221, 200, 0.25)',
  ...(selected && {
    borderTop: '1px solid rgba(124, 174, 190, 0.50)',
    borderRight: '1px solid rgba(124, 174, 190, 0.50)',
    borderLeft: '1px solid rgba(124, 174, 190, 0.50)',
  }),
}));

function tabPanelProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const DeleteButton = styled(Button)(({ theme }) => ({
  color: theme.palette.primary.main,
  borderRadius: '10px',
  background: '#FFF',
  transition: 'background-color 0.2s ease',
  padding: '12px !important',
  '&:hover': {
    background:
      'var(--Alert-Sunset-Orange, linear-gradient(175deg, #FF9A9A -9.81%, #FF5858 96.32%))', // Change color on hover
    color: '#FFF',
  },
}));

const StyledTextButton = styled(Button)(() => ({
  padding: '5px !important',
  marginLeft: '8px',
  borderRadius: '10px 10px 0px 0px',
  '& span': {
    background: 'linear-gradient(138deg, #6599A5 24.11%, #3B667E 83.2%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    fontFamily: 'Satoshi',
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '21px',
  },
  '&.active': {
    borderTop: '1px solid rgba(124, 174, 190, 0.50)',
    borderRight: '1px solid rgba(124, 174, 190, 0.50)',
    borderLeft: '1px solid rgba(124, 174, 190, 0.50)',
  },
}));

export default function NotesTabs() {
  const { deleteModalOpen, deleteNoteLoading } = useAppSelector((state) => state.note);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [tabsProperties, setTabsProperties] = useState<TabsProperties[]>([]);
  const notes = useAppSelector((state) => state.note.notes);
  const coachId = useAppSelector((state) => state.coach.coach?.id);
  const clientId = useParams<{ id: string }>().id;
  const [addNoteButton, setAddNoteButton] = useState<boolean>(false);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [hoverIcon, setHoverIcon] = useState<boolean>(false);
  const open = Boolean(anchorEl);
  const [deleteNoteLabel, setDeleteNoteLabel] = useState('');
  const deletedNoteId = useAppSelector((state) => state.note.deletedNoteId);
  const [deleteNote, setDeleteNote] = useState<TabsProperties>();
  const createdNote = useAppSelector((state) => state.note.createdNote);
  // pop over state
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<null | HTMLLIElement | HTMLDivElement>(
    null
  );
  const popoverOpen = Boolean(popoverAnchorEl);
  const popoverId = popoverOpen ? 'simple-popover' : undefined;
  const permissionCheckers = createPermissionCheckers();
  const { checkPermissions } = permissionCheckers;

  const setIndexValue = (index: number, noteId: string | null) => {
    // this is for to clear note image urls and keys when notes change
    dispatch(clearNoteCache());
    const noteData = notes?.find((note) => note.id === noteId);
    dispatch(setSelectedNoteId(noteData?.id || null));
    dispatch(setInitialHtml(noteData?.html || ''));

    setSelectedIndex(index);
  };

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setIndexValue(newValue, tabsProperties[newValue].noteId);
    const counter = tabsProperties[newValue]?.counter;
    const label = tabsProperties[newValue]?.label;
    dispatch(
      setDeleteRecordingLabel(
        counter > 1
          ? `${formatNumberToStringMonthWithoutYear(label)}(${counter})`
          : formatNumberToStringMonthWithoutYear(label)
      )
    );
  };

  function getMaxCounterForToday(currentNotes: TabsProperties[]): number | null {
    const today = new Date().toISOString().split('T')[0]; // Get today's date in ISO format (YYYY-MM-DD)
    // Filter notes for today's date
    const todayNotes = currentNotes.filter((note) => note.label.startsWith(today));
    // Get an array of counter values for today's notes
    const counterValues = todayNotes.map((note) => note.counter);
    // Return the maximum counter value for today, or null if there are no notes for today
    return counterValues.length > 0 ? Math.max(...counterValues) + 1 : null;
  }

  const handleAddTab = () => {
    dispatch(clearNoteCache());
    // Add a new tab for today
    tabsProperties.unshift({
      label: new Date().toISOString(),
      noteId: null,
      counter: getMaxCounterForToday(tabsProperties) || 1,
    });
    setTabsProperties([...tabsProperties]);
    setAddNoteButton(false);
    setIndexValue(0, tabsProperties[0].noteId);
    const { counter, label } = tabsProperties[0];
    dispatch(
      setDeleteRecordingLabel(
        counter > 1
          ? `${formatNumberToStringMonthWithoutYear(label)}(${counter})`
          : formatNumberToStringMonthWithoutYear(label)
      )
    );
  };

  const handleViewAllNotes = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const updateTabsAndNavigate = (newIndex: number) => {
    setAnchorEl(null);
    setIndexValue(newIndex, tabsProperties[newIndex].noteId);
  };

  const handleDeleteModalOpen = (isOpen: boolean) => {
    dispatch(setDeleteModalOpen(isOpen));
  };

  const handleDeleteTab = () => {
    handleDeleteModalOpen(true);
  };

  // Fetch notes when clientId and coachId are available
  useEffect(() => {
    if (!clientId || !coachId) return;
    dispatch(
      fetchNotesRequest({
        clientId,
        coachId,
        page: 1,
        pageSize: 1000,
      })
    );
  }, [coachId, clientId]);

  // Set tabsProperties when notes are available
  useEffect(() => {
    if (notes === null || tabsProperties.length > 0) {
      return;
    }
    if (notes && notes.length === 0) {
      setTabsProperties([
        {
          label: new Date().toISOString(),
          noteId: null,
          counter: 1,
        },
      ]);
      setIndexValue(0, null);
      setAddNoteButton(false);
    } else {
      const todayFormattedDate = new Date().toISOString().split('T')[0];

      // Map notes to tab properties as before
      const updatedTabsProperties = notes?.map((note) => ({
        label: note.createdAt,
        noteId: note.id,
        counter: note.counter,
      })) as TabsProperties[];

      setTabsProperties(updatedTabsProperties);

      // Check if today's date tab already exists
      const todayTabExists = updatedTabsProperties?.some(
        (tab) => tab.label.split('T')[0] === todayFormattedDate
      );
      if (!todayTabExists && updatedTabsProperties?.length > 0) {
        setTabsProperties((prevTabs) => [
          {
            label: new Date().toISOString(),
            noteId: null,
            counter: 1,
          },
          ...prevTabs,
        ]);
        setAddNoteButton(false);
        setIndexValue(0, null);
      } else {
        setIndexValue(0, updatedTabsProperties[0].noteId);
        setAddNoteButton(true);
      }
    }
  }, [notes]);

  // clear notes and note cache when component unmounts
  useEffect(
    () => () => {
      dispatch(clearNotes());
      dispatch(clearNoteCache());
    },
    []
  );

  // find the tabProperty with null note id and replace with created note id
  useEffect(() => {
    if (createdNote) {
      const index = tabsProperties.findIndex((i) => i.noteId === null);
      if (index >= 0) {
        tabsProperties[index].noteId = createdNote.id;
        setTabsProperties([...tabsProperties]);
      }
      setAddNoteButton(true);
    }
  }, [createdNote]);

  // find the deleted note id and remove from tabsProperties
  useEffect(() => {
    if (deletedNoteId) {
      const index = tabsProperties.findIndex((i) => i.noteId === deletedNoteId);
      if (index >= 0) {
        tabsProperties.splice(index, 1);
        if (tabsProperties.length === 0) {
          setTabsProperties([
            {
              label: new Date().toISOString(),
              noteId: null,
              counter: 1,
            },
          ]);
          setAddNoteButton(false);
        } else {
          setTabsProperties([...tabsProperties]);
        }
        setIndexValue(0, tabsProperties[0]?.noteId || null);
      }
    }
  }, [deletedNoteId]);

  const handleContextMenu = (
    event: React.MouseEvent<HTMLLIElement | HTMLDivElement, MouseEvent>,
    tabIndex: number
  ) => {
    const noteData = notes?.find((note) => note.id === tabsProperties[tabIndex].noteId);
    if (!noteData || !checkPermissions(UserPermissionType.NOTE_MODULE_DELETE_NOTES)) return;
    event.preventDefault();
    setPopoverAnchorEl(event.currentTarget);
    setDeleteNote(tabsProperties[tabIndex]);
    const counter = tabsProperties[tabIndex]?.counter;
    const label = tabsProperties[tabIndex]?.label;
    setDeleteNoteLabel(
      counter > 1
        ? `${formatNumberToStringMonthWithoutYear(label)}(${counter})`
        : formatNumberToStringMonthWithoutYear(label)
    );
  };

  const handleDismiss = () => {
    handleDeleteModalOpen(false);
  };

  const handleDeleteNote = () => {
    if (deleteNote) {
      dispatch(
        deleteSingleNoteRequest({
          id: deleteNote.noteId as string,
          clientId: clientId as string,
          coachId: coachId as string,
        })
      );
    }
    setPopoverAnchorEl(null);
  };

  const generateLabel = (counter: number, label: string) =>
    counter > 1
      ? `${formatNumberToStringMonthWithoutYear(label)} (${counter})`
      : formatNumberToStringMonthWithoutYear(label);

  return (
    <Box display="flex" flexDirection="column" height="100%" sx={{ background: 'transparent' }}>
      {(!tabsProperties || tabsProperties.length === 0) && <NotesLoading />}
      <Box display="flex" flexDirection="row" gap="5px" sx={{ background: 'transparent' }}>
        {addNoteButton &&
          tabsProperties &&
          checkPermissions(UserPermissionType.NOTE_MODULE_CREATE_AND_EDIT_NOTES) && (
            <Box
              onClick={handleAddTab}
              display="flex"
              alignItems="center"
              sx={{
                cursor: 'pointer',
                boxShadow: '0px 4px 40px 0px rgba(255, 221, 200, 0.25)',
              }}
            >
              <img src="/assets/icons/add-note.svg" alt="add-note" />
            </Box>
          )}
        <Tabs
          value={selectedIndex > 7 ? false : selectedIndex}
          onChange={handleChange}
          aria-label="basic tabs example"
          centered
          variant="fullWidth"
          sx={{
            minHeight: '34px',
            '& .MuiTabs-indicator': {
              display: 'none',
            },
            boxShadow: '0px 4px 40px 0px rgba(255, 221, 200, 0.25)',
          }}
        >
          {tabsProperties
            ?.slice(0, 7)
            .map((singleTab: { label: string; counter: number }, index) => (
              <SingleTab
                key={singleTab.label}
                label={
                  singleTab.counter > 1
                    ? `${formatNumberToStringMonthWithoutYear(singleTab.label)} (${
                        singleTab.counter
                      })`
                    : formatNumberToStringMonthWithoutYear(singleTab.label)
                }
                {...tabPanelProps(index)}
                selected={selectedIndex === index}
                onContextMenu={(e) => handleContextMenu(e, index)}
              />
            ))}
          {tabsProperties?.length > 7 && (
            <StyledTextButton
              onClick={handleViewAllNotes}
              variant="text"
              className={selectedIndex >= 7 ? 'active' : ''}
              sx={{ '&.MuiButton-root:hover': { bgcolor: 'transparent' } }}
            >
              <span>
                {selectedIndex >= 7
                  ? generateLabel(
                      tabsProperties[selectedIndex].counter,
                      tabsProperties[selectedIndex].label
                    )
                  : t('notes.viewAllNotes')}
              </span>
            </StyledTextButton>
          )}
        </Tabs>
      </Box>
      <Box height="95%">{tabsProperties?.length > 0 && <RichTextEditor />}</Box>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => {
          setAnchorEl(null);
        }}
      >
        {tabsProperties?.slice(7).map((singleTab: { label: string; counter: number }, index) => (
          <MenuItem
            key={singleTab.label}
            selected={selectedIndex >= 7 && selectedIndex - 7 === index}
            onClick={() => updateTabsAndNavigate(index + 7)}
            onContextMenu={(e) => handleContextMenu(e, index + 7)}
          >
            <Typography variant="bodyRegular" color="primary">
              {singleTab.counter > 1
                ? `${formatNumberToStringMonthWithoutYear(singleTab.label)} (${singleTab.counter})`
                : formatNumberToStringMonthWithoutYear(singleTab.label)}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
      <Popover
        id={popoverId}
        open={popoverOpen}
        anchorEl={popoverAnchorEl}
        onClose={() => setPopoverAnchorEl(null)}
        PaperProps={{
          style: {
            borderRadius: '0',
            boxShadow: 'none',
            border: 'none',
          },
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <DeleteButton
          type="submit"
          variant="contained"
          fullWidth
          onClick={handleDeleteTab}
          onMouseEnter={() => setHoverIcon(true)}
          onMouseLeave={() => setHoverIcon(false)}
          startIcon={
            hoverIcon ? (
              <img src="/assets/icons/trash-bin-white.svg" alt="trash-bin-white" />
            ) : (
              <img src="/assets/icons/trash-bin-red.svg" alt="trash-bin-red" />
            )
          }
        >
          {t('notes.deleteNote')}
        </DeleteButton>
      </Popover>
      {deleteModalOpen && (
        <DeleteNoteConfirmation
          handleDelete={handleDeleteNote}
          handleDismiss={handleDismiss}
          name={deleteNoteLabel}
          deleteLoading={deleteNoteLoading}
          deleteModalOpen={deleteModalOpen}
        />
      )}
    </Box>
  );
}
